import React from "react";
import { hasValue, toKebabCase, toCssValue } from "@utils";
/**
 * Map of Phosphor prefix to icon weight
 */
export const PHIconPrefixWeightMap = {
    'pht': 'thin',
    'phl': 'light',
    'phr': 'regular',
    'phb': 'bold',
    'phf': 'fill',
    'phd': 'duotone',
};
/**
 * Map FontAwesome size prop to em values
 * Used for converting size props to CSS values for Phosphor and other icon types
 */
export const SizePropToEmMap = {
    '2xs': '0.625em',
    'xs': '0.75em',
    'sm': '0.875em',
    '1x': '1em',
    'lg': '1.25em',
    'xl': '1.5em',
    '2xl': '2em',
    '2x': '2em',
    '3x': '3em',
    '4x': '4em',
    '5x': '5em',
    '6x': '6em',
    '7x': '7em',
    '8x': '8em',
    '9x': '9em',
    '10x': '10em',
};
/**
 * @export
 */
export function hasIcon(icon) {
    if (typeof icon === 'string')
        return hasValue(icon);
    if (Array.isArray(icon))
        return icon.length === 2;
    return !!icon; // React.ReactElement
}
/**
 * @export
 * Check if icon is FontAwesome format (string or array)
 * @example isFontawesomeIcon('fas save') // true
 * @example isFontawesomeIcon('fas:save') // true
 * @example isFontawesomeIcon(['fas', 'save']) // true
 * @example isFontawesomeIcon('phb house') // false
 */
export function isFontawesomeIcon(icon) {
    if (!hasIcon(icon))
        return false;
    const fontawesomePrefixRegex = /^fa[bdlrs]$/;
    const fontawesomeStringRegex = /fa[bdlrs][\s:][a-zA-Z-0-9]+/g;
    if (typeof icon === 'string')
        return fontawesomeStringRegex.test(icon === null || icon === void 0 ? void 0 : icon.trim());
    if (Array.isArray(icon)) {
        if (icon.length !== 2)
            return false;
        const [prefix, iconName] = icon;
        return typeof prefix === 'string' &&
            typeof iconName === 'string' &&
            fontawesomePrefixRegex.test(prefix) &&
            iconName.length > 0;
    }
    return false;
}
/**
 * @export
 * Convert FontAwesome string to IconProp array format
 * @example getFontAwesomeIcon('fas save') // ['fas', 'save']
 * @example getFontAwesomeIcon('fas:save') // ['fas', 'save']
 */
export function getFontAwesomeIcon(icon) {
    if (!icon)
        return null;
    return icon.split(/[\s:]+/);
}
/**
 * @export
 * Check if icon is Phosphor format (string or array)
 * @example isPhosphorIcon('phb house') // true
 * @example isPhosphorIcon('phb:house') // true
 * @example isPhosphorIcon(['phb', 'house']) // true
 * @example isPhosphorIcon('fas save') // false
 */
export function isPhosphorIcon(icon) {
    if (!hasIcon(icon))
        return false;
    const phosphorStringRegex = /^ph[rthlbfd][\s:]+[a-zA-Z-]+$/;
    if (typeof icon === 'string')
        return phosphorStringRegex.test(icon.trim());
    if (Array.isArray(icon)) {
        if (icon.length !== 2)
            return false;
        const [prefix, iconName] = icon;
        return typeof prefix === 'string' &&
            typeof iconName === 'string' &&
            prefix in PHIconPrefixWeightMap &&
            iconName.length > 0;
    }
    return false;
}
/**
 * @export
 * Parse Phosphor icon string to PHIconProp array format
 * @example getPhosphorIcon('phb house') // ['phb', 'house']
 * @example getPhosphorIcon('phb:house') // ['phb', 'house']
 * @example getPhosphorIcon('phr heart') // ['phr', 'heart']
 */
export function getPhosphorIcon(icon) {
    if (!icon)
        return null;
    const parts = icon.trim().split(/[\s:]+/);
    if (parts.length === 2) {
        if (parts[0] in PHIconPrefixWeightMap) {
            return [parts[0], parts[1]];
        }
        return parts[1]; // return only icon name
    }
    if (parts.length === 1)
        return icon.trim(); // assume only icon name is passed
    return null;
}
/**
 * @export
 * Check if string is an image URL (http/https/data URI/relative path)
 * @example isImageUrl('https://example.com/icon.svg') // true
 * @example isImageUrl('data:image/svg+xml;base64,...') // true
 * @example isImageUrl('/assets/icon.png') // true
 * @example isImageUrl('./images/logo.svg') // true
 * @example isImageUrl('../icons/star.png') // true
 * @example isImageUrl('icon-name') // false
 */
export function isImageUrl(str) {
    if (!str || typeof str !== 'string')
        return false;
    const trimmed = str.trim();
    // Check for absolute URLs (http/https) or data URIs
    const urlRegex = /^(https?:\/\/|data:image\/)/i;
    if (urlRegex.test(trimmed))
        return true;
    // Check for relative paths
    // Starts with: /, ./, ../ or contains a file extension
    const relativePathRegex = /^(\/|\.\/|\.\.\/)|(\.(png|jpe?g|gif|svg|webp|bmp|ico)(\?|#|$))/i;
    return relativePathRegex.test(trimmed);
}
/**
 * @export
 * Parse any icon prop and determine its type and format
 * Returns formatted icon ready for the appropriate wrapper
 *
 * @example parseIcon('fas save') // { type: 'fontawesome', formatted: ['fas', 'save'], original: 'fas save' }
 * @example parseIcon(['phb', 'house']) // { type: 'phosphor', formatted: ['phb', 'house'], original: ['phb', 'house'] }
 * @example parseIcon('https://example.com/icon.svg') // { type: 'image', formatted: 'https://...', original: 'https://...' }
 */
export function parseIcon(icon) {
    if (!hasIcon(icon))
        return null;
    // Phosphor icon (string or array)
    if (isPhosphorIcon(icon)) {
        return {
            type: 'phosphor',
            formatted: (Array.isArray(icon) ? icon : getPhosphorIcon(icon)),
            original: icon
        };
    }
    // FontAwesome icon (string or array)
    if (isFontawesomeIcon(icon)) {
        return {
            type: 'fontawesome',
            formatted: (Array.isArray(icon) ? icon : getFontAwesomeIcon(icon)),
            original: icon
        };
    }
    // String formats (image or text)
    if (typeof icon === 'string') {
        // Image URL
        if (isImageUrl(icon)) {
            return {
                type: 'image',
                formatted: icon,
                original: icon
            };
        }
        // Default to text/emoji
        return {
            type: 'text',
            formatted: icon,
            original: icon
        };
    }
    // React element
    else if (React.isValidElement(icon)) {
        return {
            type: 'react',
            formatted: icon,
            original: icon
        };
    }
    return null;
}
/**
 * @export
 * Parse PHIconProp to extract weight and icon name
 * Used internally by PHIconWrapper
 * @example parseIconProp(['phb', 'House']) // { weight: 'bold', iconName: 'house' }
 * @example parseIconProp('ArrowRight') // { weight: 'regular', iconName: 'arrow-right' }
 */
export function parsePhosphorIcon(icon) {
    if (Array.isArray(icon)) {
        const [prefix, iconName] = icon;
        const weight = PHIconPrefixWeightMap[prefix] || 'regular';
        return { weight, iconName: toKebabCase(iconName) };
    }
    // Single string - use regular weight
    return { weight: 'regular', iconName: toKebabCase(icon) };
}
// ============================================================================
// Styling Utilities
// ============================================================================
/**
 * Convert size prop to CSS size value
 * Supports FontAwesome size props, numbers, or CSS strings
 * @example getSizeValue('2x') // '2em'
 * @example getSizeValue(24) // 24
 * @example getSizeValue('2rem') // '2rem'
 */
export function getSizeValue(size, isIcon = true) {
    if (!size)
        return isIcon ? '1em' : null;
    // Check if it's a FontAwesome size prop
    if (typeof size === 'string' && size in SizePropToEmMap) {
        return SizePropToEmMap[size];
    }
    return size;
}
/**
 * @export
 * Resolve the width/height for an icon's loading skeleton so it tracks the icon's
 * own size instead of filling the parent.
 * - explicit `width`/`height` (image icons) win
 * - else the `size` prop (FA token → em, number/CSS string passthrough)
 * - else falls back to `2rem`
 */
export function resolveIconSkeletonSize(opts) {
    var _a, _b, _c, _d;
    const sizeVal = getSizeValue(opts.size, false); // null when no size given
    return {
        width: toCssValue((_b = (_a = opts.width) !== null && _a !== void 0 ? _a : sizeVal) !== null && _b !== void 0 ? _b : '1.5rem'),
        height: toCssValue((_d = (_c = opts.height) !== null && _c !== void 0 ? _c : sizeVal) !== null && _d !== void 0 ? _d : '1.5rem'),
    };
}
/**
 * Get animation class names based on animation props
 * Uses uxp-icon-* prefix for framework-agnostic naming
 * @example getAnimationClasses({ spin: true, beat: true }) // ['uxp-icon-spin', 'uxp-icon-beat']
 */
export function getAnimationClasses(props) {
    const classes = [];
    if (props.spin)
        classes.push('uxp-icon-spin');
    if (props.pulse)
        classes.push('uxp-icon-pulse');
    if (props.beat)
        classes.push('uxp-icon-beat');
    if (props.bounce)
        classes.push('uxp-icon-bounce');
    if (props.shake)
        classes.push('uxp-icon-shake');
    if (props.fade)
        classes.push('uxp-icon-fade');
    if (props.border)
        classes.push('uxp-icon-border');
    if (props.fixedWidth)
        classes.push('uxp-icon-fw');
    return classes;
}
/**
 * Get transform style for flip and rotation
 * @example getTransformStyle('horizontal') // 'scaleX(-1)'
 * @example getTransformStyle('vertical', 90) // 'scaleY(-1) rotate(90deg)'
 */
export function getTransformStyle(flip, rotation) {
    const transforms = [];
    if (flip === 'horizontal') {
        transforms.push('scaleX(-1)');
    }
    else if (flip === 'vertical') {
        transforms.push('scaleY(-1)');
    }
    else if (flip === 'both') {
        transforms.push('scale(-1, -1)');
    }
    if (rotation) {
        transforms.push(`rotate(${rotation}deg)`);
    }
    return transforms.length > 0 ? transforms.join(' ') : undefined;
}
